import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import Levels from './Levels'
import StatusCountBar from './StatusCountBar'
import { NameContainer, TotalContainer } from './_Styled'
import Caption1 from '../typography/captions/Caption1'
import Caption2 from '../typography/captions/Caption2'
import Caption5 from '../typography/captions/Caption5'
import H2 from '../typography/headers/H2'
import Intro2 from '../typography/Intro2'
import P3 from '../typography/paragraphs/P3'
import { getTruncatedString } from '../../utils/string'
import { formatNumberForThreeDigits } from '../../utils/numbers'
import { logLevelsExpandClick } from '../../actions/productionstatus'

const CourseWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 73px;
`

const StatsCountWrapper = styled(TotalContainer)`
  flex: 0 0 72px;
  padding-bottom: 10px;
  flex-direction: column;
  margin-right: 10px;
  box-sizing: border-box;
  max-width: 72px;
`

const StatusCountBarContainer = styled.div`
  position: absolute;
  right: 28px;
  top: 58px;
`

const StyledNameContainer = styled(NameContainer)`
  justify-content: center;
  flex-direction: column;
  flex: 0 0 232px;
  padding: 0 9px 0 22px;
`

const StyledCaption1 = styled(Caption1)`
  font-weight: ${props => (props.bold ? '500' : '300')};
`

const CourseRow = styled.li`
  position: relative;
  border-top: ${props => (props.expanded ? '1px solid #DEDFE0' : 'none')};
  border-bottom: ${props => (props.expanded ? '1px solid #DEDFE0' : 'none')};

  &:first-of-type {
    border-top: none;
  }

  &:last-of-type {
    border-bottom: none;
  }
`

const StyledCaption5 = styled(Caption5)`
  white-space: nowrap;
`

class Course extends React.Component {
  constructor() {
    super()
    this.state = {
      expanded: false,
      hovered: false
    }

    this.onCourseClickBound = this.onCourseClick.bind(this)
    this.onCourseHoverBound = this.onCourseHover.bind(this)
    this.onCourseLeaveHoverBound = this.onCourseLeaveHover.bind(this)
  }

  render() {
    const { course, clickHandler } = this.props

    return (
      <CourseRow expanded={this.state.expanded}>
        <CourseWrapper>
          <StyledNameContainer
            style={{ paddingLeft: '21px' }}
            onClick={this.onCourseClickBound}
            onMouseEnter={this.onCourseHoverBound}
            onMouseLeave={this.onCourseLeaveHoverBound}
            onFocus={this.onCourseHoverBound}
            onBlur={this.onCourseLeaveHoverBound}
          >
            <Caption2
              style={{
                color: `#${course.color}`,
                backgroundColor: 'unset'
              }}
            >
              {getTruncatedString({
                val: course.course_name,
                len: 50
              })}
            </Caption2>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <StyledCaption1 bold={this.state.expanded || this.state.hovered}>
                {`${course.course_levels.length} Levels`}
              </StyledCaption1>
              {(this.state.hovered || this.state.expanded) && (
                <div
                  className={`expand-arrow ${
                    this.state.expanded ? 'expanded' : ''
                  }`}
                />
              )}
            </div>
          </StyledNameContainer>
          <TotalContainer
            style={{ marginRight: '8px' }}
            onClick={() =>
              clickHandler({
                course: course
              })
            }
          >
            <H2>{formatNumberForThreeDigits(course.total_count)}</H2>
          </TotalContainer>

          <StatsCountWrapper
            onClick={() =>
              clickHandler({
                status: 'draft',
                course: course
              })
            }
          >
            <Intro2>{formatNumberForThreeDigits(course.draft_count)}</Intro2>
            <StyledCaption5>Draft</StyledCaption5>
          </StatsCountWrapper>

          <StatsCountWrapper
            onClick={() =>
              clickHandler({
                status: 'exp/sub',
                course: course
              })
            }
          >
            <P3 style={{ paddingTop: '10px' }}>
              {formatNumberForThreeDigits(course.internal_review_count)}
            </P3>
            <StyledCaption5 style={{ paddingTop: '1px' }}>
              Experience/Subject
            </StyledCaption5>
          </StatsCountWrapper>

          <StatsCountWrapper
            onClick={() =>
              clickHandler({
                status: 'copy edit',
                course: course
              })
            }
          >
            <P3 style={{ paddingTop: '10px' }}>
              {formatNumberForThreeDigits(course.copy_edit_count)}
            </P3>
            <StyledCaption5 style={{ paddingTop: '1px' }}>
              Copy Edit
            </StyledCaption5>
          </StatsCountWrapper>

          <StatsCountWrapper
            onClick={() =>
              clickHandler({
                status: 'review returned',
                course: course
              })
            }
            style={{ marginRight: '22px' }}
          >
            <Intro2>{formatNumberForThreeDigits(course.rejected_count)}</Intro2>
            <StyledCaption5>Review Returned</StyledCaption5>
          </StatsCountWrapper>

          <StatsCountWrapper
            onClick={() =>
              clickHandler({
                status: 'quality review',
                course: course
              })
            }
            style={{ marginRight: '22px' }}
          >
            <Intro2>
              {formatNumberForThreeDigits(course.quality_review_count)}
            </Intro2>
            <StyledCaption5>Publication Review</StyledCaption5>
          </StatsCountWrapper>

          <StatsCountWrapper
            style={{ marginRight: '13px' }}
            onClick={() =>
              clickHandler({
                status: 'published',
                course: course
              })
            }
          >
            <Intro2>
              {formatNumberForThreeDigits(course.published_count)}
            </Intro2>
            <StyledCaption5>Published</StyledCaption5>
          </StatsCountWrapper>
        </CourseWrapper>
        <StatusCountBarContainer>
          <StatusCountBar
            draftCount={course.draft_count}
            rejectedCount={course.rejected_count}
            qualityReviewCount={course.quality_review_count}
            publishedCount={course.published_count}
            totalCount={course.total_count}
            width={480}
            height={8}
          />
        </StatusCountBarContainer>
        {this.state.expanded && (
          <Levels
            course={course}
            levels={course.course_levels}
            color={course.color}
            clickHandler={clickHandler}
            levelFilters={this.props.levelFilters}
          />
        )}
      </CourseRow>
    )
  }

  onCourseClick() {
    // if current state is !expanded, log analytics event
    if (!this.state.expanded) {
      this.props.dispatch(logLevelsExpandClick(this.props.course))
    }
    this.setState(state => ({
      expanded: !state.expanded
    }))
  }

  onCourseHover() {
    this.setState(state => ({
      hovered: true
    }))
  }

  onCourseLeaveHover() {
    this.setState(state => ({
      hovered: false
    }))
  }
}

Course.propTypes = {
  course: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  clickHandler: PropTypes.func.isRequired,
  levelFilters: PropTypes.array.isRequired
}

export default Course



// WEBPACK FOOTER //
// ./src/components/productionstatus/Course.js